<template>
  <Breadcrumb :breadcrumb="breadcrumbData" id="breadcrumb" />
</template>

<script>
import Breadcrumb from '@/components/breadcrumb/Breadcrumb.vue';
import { computed, defineAsyncComponent } from 'vue';
import useAsyncComputed from '@/compositions/useAsyncComputed.js';
import useHead from '@/compositions/useHead';
import StructuredOrganization from '@/components/meta/StructuredOrganization.js';
import buildSocialMetaTags from '@/components/meta/Social.js';
import { useRouter } from 'vue-router';
import { resolveEntryMutating } from '@/services/ContentfulService.js';

export default {
  name: 'CampaignPageMeta',
  components: {
    Breadcrumb,
  },
  props: {
    entry: {
      type: Object,
      required: true,
    },
    isPreview: {
      type: Boolean,
      required: true,
    },
    slug: {
      type: String,
      required: false,
    },
  },
  async setup(props) {
    const router = useRouter();
    const promises = [];

    const [seoImage, loadingSeoImage, initSeoImage] = useAsyncComputed(
      async () => (await resolveEntryMutating(props.entry.fields.seoImage, 2, props.isPreview))?.fields?.file,
    );
    promises.push(initSeoImage);

    const metaTags = computed(() => [
      {
        name: 'description',
        content: props.entry.fields.description,
      },
      ...(props.entry.fields.noindex
        ? [
            {
              name: 'robots',
              content: 'noindex',
            },
          ]
        : []),
      ...buildSocialMetaTags({
        title: props.entry.fields.title,
        description: props.entry.fields.description,
        url: `${import.meta.env.VITE_STOREFRONT_BASE_URL}${router.currentRoute.value.path}`,
        image: seoImage.value,
      }),
    ]);

    useHead(
      {
        title: props.entry.fields.title,
        script: [{ type: 'application/ld+json', children: JSON.stringify(StructuredOrganization) }],
        meta: computed(() => metaTags.value),
      },
      { appendTitleSuffix: false },
    );

    const parentBreadcrumb = computed(() => {
      if (props.entry.fields?.breadCrumbParentLink) {
        return {
          title: props.entry.fields?.breadCrumbParentTitle,
          url: props.entry.fields?.breadCrumbParentLink,
        };
      } else if (props.entry.fields?.breadCrumbParentTitle) return { title: props.entry.fields?.breadCrumbParentTitle };
      else return {};
    });

    const breadcrumbData = computed(() => {
      let data = props.entry.fields.breadcrumbTitle
        ? [
            {
              title: 'Home',
              url: import.meta.env.VITE_STOREFRONT_BASE_URL,
            },
            {
              title: props.entry.fields.breadcrumbTitle,
              url: `${import.meta.env.VITE_STOREFRONT_BASE_URL}${router.currentRoute.value.path}`,
            },
          ]
        : [];

      if (data.length > 0 && Object.keys(parentBreadcrumb.value).length > 0) {
        data.splice(1, 0, parentBreadcrumb.value);
      }
      return data;
    });

    await Promise.all(promises);
    return {
      breadcrumbData,
    };
  },
};
</script>

<style lang="postcss">
.breadcrumb {
  margin-bottom: var(--space-4);
}
</style>
